import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '../../../ui/icons/CloseIcon';
import { UnitTable } from './UnitTable';
import type { Floorplan, Unit } from './UnitTable';
import * as styles from './unitsModule.styles';

type SortOption = 'lowest-price' | 'highest-price' | 'newest';

const SORT_LABELS: Record<SortOption, string> = {
  'lowest-price': 'Lowest price',
  'highest-price': 'Highest price',
  newest: 'Newest',
};

const SORT_OPTIONS: Array<SortOption> = ['lowest-price', 'highest-price', 'newest'];

type SeeAllUnitsOverlayProps = {
  floorplan: Floorplan;
  open: boolean;
  onClose: () => void;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
  isMobile: boolean;
};

const getBedroomHeaderLabel = (beds: number): string => {
  if (beds === 0) return 'Studio';
  if (beds === 1) return 'One-bedroom';
  if (beds === 2) return 'Two-bedroom';
  if (beds === 3) return 'Three-bedroom';
  return `${beds}-bedroom`;
};

const getFloorplanImageUrl = (floorplan: Floorplan): string | null => {
  const layoutCount = floorplan.layoutCount ?? 0;
  if (layoutCount === 0) return null;
  if (floorplan.largePhotos?.length > 0) return floorplan.largePhotos[0].url;
  if (floorplan.photos?.length > 0) return floorplan.photos[0].url;
  return null;
};

const sortUnits = (units: Array<Unit>, sortBy: SortOption): Array<Unit> => {
  const sorted = [...units];
  switch (sortBy) {
    case 'lowest-price':
      return sorted.sort((a, b) => (a.low || Infinity) - (b.low || Infinity));
    case 'highest-price':
      return sorted.sort((a, b) => (b.low || 0) - (a.low || 0));
    case 'newest':
      return sorted.sort((a, b) => {
        if (a.isNewUnit && !b.isNewUnit) return -1;
        if (!a.isNewUnit && b.isNewUnit) return 1;
        return 0;
      });
    default:
      return sorted;
  }
};

// Custom dropdown following WAI-ARIA Listbox pattern
// Ref: https://www.w3.org/WAI/ARIA/apg/patterns/listbox/
const LISTBOX_ID = 'sort-listbox';

const SortDropdown = ({ value, onChange }: { value: SortOption; onChange: (v: SortOption) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Focus the selected option when dropdown opens
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const selectedOption = listRef.current.querySelector('[aria-selected="true"]') as HTMLElement | null;
    if (selectedOption) {
      selectedOption.focus();
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  const handleSelect = useCallback(
    (option: SortOption) => {
      onChange(option);
      setIsOpen(false);
      triggerRef.current?.focus();
    },
    [onChange],
  );

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
        e.preventDefault();
        openDropdown();
        break;
      case 'ArrowUp':
        e.preventDefault();
        openDropdown();
        break;
    }
  };

  const focusOptionByOffset = (current: Element | null, offset: number) => {
    if (!listRef.current) return;
    const options = Array.from(listRef.current.querySelectorAll('[role="option"]')) as Array<HTMLElement>;
    const currentIndex = current ? options.indexOf(current as HTMLElement) : -1;
    const nextIndex = Math.max(0, Math.min(options.length - 1, currentIndex + offset));
    options[nextIndex]?.focus();
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    const active = document.activeElement;
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        break;
      case 'ArrowDown':
        e.preventDefault();
        focusOptionByOffset(active, 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusOptionByOffset(active, -1);
        break;
      case 'Home':
        e.preventDefault();
        focusOptionByOffset(null, 0);
        break;
      case 'End':
        e.preventDefault();
        if (listRef.current) {
          const options = listRef.current.querySelectorAll('[role="option"]');
          (options[options.length - 1] as HTMLElement)?.focus();
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (active?.getAttribute('role') === 'option') {
          const optionValue = active.getAttribute('data-value') as SortOption;
          if (optionValue) handleSelect(optionValue);
        }
        break;
      case 'Tab':
        closeDropdown();
        break;
    }
  };

  return (
    <div ref={containerRef} className={styles.sortContainer}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.sortTrigger}
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? LISTBOX_ID : undefined}
        aria-label={`Sort by ${SORT_LABELS[value]}`}
      >
        <span className={styles.sortLabel}>Sort by:</span>
        <span className={styles.sortValue}>{SORT_LABELS[value]}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={styles.sortChevron}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div
          ref={listRef}
          id={LISTBOX_ID}
          role="listbox"
          aria-label="Sort options"
          className={styles.sortDropdownPanel}
          onKeyDown={handleListKeyDown}
        >
          {SORT_OPTIONS.map((option) => {
            const isSelected = option === value;
            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-value={option}
                className={isSelected ? styles.sortOptionSelected : styles.sortOption}
                onClick={() => handleSelect(option)}
                tabIndex={-1}
              >
                {SORT_LABELS[option]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FloorPlanImageOrPlaceholder = ({ imageUrl, name }: { imageUrl: string | null; name: string }) => {
  if (imageUrl) {
    return (
      <div className={styles.sheetImageBorder}>
        <div className={styles.sheetImagePadding}>
          <img src={imageUrl} alt={`Floor plan ${name}`} className={styles.bottomSheetImage} />
        </div>
      </div>
    );
  }
  return (
    <div className={styles.sheetImageBorder}>
      <div className={styles.sheetImagePadding}>
        <div className={styles.sheetPlaceholderInner}>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" style={{ color: '#d0d9d8' }}>
            <rect x="4" y="4" width="24" height="24" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <line x1="4" y1="16" x2="28" y2="16" stroke="currentColor" strokeWidth="1.5" />
            <line x1="16" y1="4" x2="16" y2="28" stroke="currentColor" strokeWidth="1.5" />
            <line x1="16" y1="16" x2="16" y2="28" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
            <rect x="8" y="8" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1" fill="none" />
          </svg>
          <span className={styles.sheetPlaceholderText}>No floor plan yet</span>
          <span className={styles.sheetPlaceholderSubtext}>Yeah, we wish we had it too.</span>
        </div>
      </div>
    </div>
  );
};

const BottomSheetHeaderTitle = ({ floorplan }: { floorplan: Floorplan }) => {
  const label = getBedroomHeaderLabel(floorplan.beds);
  const unitCount = floorplan.units?.length ?? 0;
  const sqft = floorplan.sqft ? `${floorplan.sqft.toLocaleString('en-US')} sqft` : '';

  return (
    <p className={styles.bottomSheetTitleText}>
      {label} units <span className={styles.bottomSheetTitleSeparator}>{'\u2022'}</span>{' '}
      <span className={styles.bottomSheetTitleDetail}>{unitCount} available</span>
      {sqft && (
        <>
          {' '}
          <span className={styles.bottomSheetTitleSeparator}>{'\u2022'}</span>{' '}
          <span className={styles.bottomSheetTitleDetail}>{sqft}</span>
        </>
      )}
    </p>
  );
};

const ModalHeaderTitle = ({ floorplan }: { floorplan: Floorplan }) => {
  const label = getBedroomHeaderLabel(floorplan.beds);
  const unitCount = floorplan.units?.length ?? 0;
  const sqft = floorplan.sqft ? `${floorplan.sqft.toLocaleString('en-US')} sqft` : '';

  return (
    <>
      <span>{label} units</span>
      <span className={styles.modalTitleDetail}>
        {' \u00B7 '}
        {unitCount} available
        {sqft && (
          <>
            {' \u00B7 '}
            {sqft}
          </>
        )}
      </span>
    </>
  );
};

// Shared hook for ESC key + body scroll lock
const useOverlayBehavior = (open: boolean, onClose: () => void, ref: React.RefObject<HTMLElement | null>) => {
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && ref.current) {
      ref.current.focus();
    }
  }, [open, ref]);
};

// Desktop: centered modal with two-column layout
const DesktopModal = ({ floorplan, open, onClose, onUnitSelect }: Omit<SeeAllUnitsOverlayProps, 'isMobile'>) => {
  const [sortBy, setSortBy] = useState<SortOption>('lowest-price');
  const modalRef = useRef<HTMLDivElement>(null);
  const imageUrl = getFloorplanImageUrl(floorplan);
  const sortedUnits = useMemo(() => sortUnits(floorplan.units ?? [], sortBy), [floorplan.units, sortBy]);

  useOverlayBehavior(open, onClose, modalRef);

  if (!open) return null;

  return createPortal(
    <div className={styles.modalBackdrop} onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${getBedroomHeaderLabel(floorplan.beds)} units`}
        tabIndex={-1}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <ModalHeaderTitle floorplan={floorplan} />
          </h2>
          <button type="button" className={styles.modalCloseButton} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.modalImageColumn}>
            <FloorPlanImageOrPlaceholder imageUrl={imageUrl} name={floorplan.name || ''} />
          </div>
          <div className={styles.modalTableColumn}>
            <SortDropdown value={sortBy} onChange={setSortBy} />
            <UnitTable units={sortedUnits} floorplan={floorplan} onUnitSelect={onUnitSelect} />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const SWIPE_CLOSE_THRESHOLD = 100;

// Mobile: bottom sheet sliding up from below
const MobileBottomSheet = ({ floorplan, open, onClose, onUnitSelect }: Omit<SeeAllUnitsOverlayProps, 'isMobile'>) => {
  const [sortBy, setSortBy] = useState<SortOption>('lowest-price');
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(open);
  const [dragOffset, setDragOffset] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const isDragging = useRef(false);
  const closeCalledRef = useRef(false);
  const imageUrl = getFloorplanImageUrl(floorplan);
  const sortedUnits = useMemo(() => sortUnits(floorplan.units ?? [], sortBy), [floorplan.units, sortBy]);

  const finishClose = useCallback(() => {
    if (closeCalledRef.current) return;
    closeCalledRef.current = true;
    setIsClosing(false);
    setIsVisible(false);
    setDragOffset(0);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    closeCalledRef.current = false;
    setIsClosing(true);
    setTimeout(finishClose, 300);
  }, [isClosing, finishClose]);

  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      finishClose();
    }
  }, [isClosing, finishClose]);

  useEffect(() => {
    if (open) {
      closeCalledRef.current = false;
      setIsVisible(true);
      setIsClosing(false);
      setDragOffset(0);
    }
  }, [open]);

  useOverlayBehavior(isVisible && !isClosing, handleClose, panelRef);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose],
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = true;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (deltaY > 0) {
      setDragOffset(deltaY);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;

    if (dragOffset > SWIPE_CLOSE_THRESHOLD) {
      handleClose();
    }
    setDragOffset(0);
  }, [dragOffset, handleClose]);

  if (!isVisible) return null;

  const panelStyle =
    dragOffset > 0 && !isClosing ? { transform: `translateY(${dragOffset}px)`, transition: 'none' } : undefined;

  return createPortal(
    <div
      className={styles.bottomSheetOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={`${getBedroomHeaderLabel(floorplan.beds)} units`}
      onClick={handleBackdropClick}
    >
      <div className={isClosing ? styles.bottomSheetBackdropClosing : styles.bottomSheetBackdrop} aria-hidden="true" />

      <div
        ref={panelRef}
        className={isClosing ? styles.bottomSheetPanelClosing : styles.bottomSheetPanel}
        tabIndex={-1}
        onAnimationEnd={handleAnimationEnd}
        style={panelStyle}
      >
        <div
          className={styles.bottomSheetStickyHeader}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className={styles.bottomSheetDragHandle}>
            <div className={styles.bottomSheetDragBar} />
          </div>
          <div className={styles.bottomSheetTitle}>
            <BottomSheetHeaderTitle floorplan={floorplan} />
          </div>
        </div>

        <div className={styles.bottomSheetBody}>
          <div className={styles.bottomSheetBodyInner}>
            <FloorPlanImageOrPlaceholder imageUrl={imageUrl} name={floorplan.name || ''} />

            <SortDropdown value={sortBy} onChange={setSortBy} />

            <UnitTable units={sortedUnits} floorplan={floorplan} onUnitSelect={onUnitSelect} />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const SeeAllUnitsOverlay = (props: SeeAllUnitsOverlayProps) => {
  if (props.isMobile) {
    return <MobileBottomSheet {...props} />;
  }
  return <DesktopModal {...props} />;
};
